import React, { useState } from 'react';
import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Box,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Storage as StorageIcon,
  MusicNote as MusicNoteIcon,
} from '@mui/icons-material';
import { DetectedProject } from '../../../../utils/projectDetection';
import { ProjectSettingsMap } from '../../../../models/batchProjectUpload';
import { DAWType } from '../../DAWLogo';

interface ProjectSelectionMenuProps {
  selectedProjects: Set<string>;
  detectedProjects: DetectedProject[];
  projectSettings: ProjectSettingsMap;
  toggleAll: () => void;
  selectProjects: (folderPaths: string[]) => void;
  deselectProjects: (folderPaths: string[]) => void;
  allSelected: boolean;
  isUploading?: boolean;
}

const ProjectSelectionMenu: React.FC<ProjectSelectionMenuProps> = ({
  detectedProjects,
  projectSettings,
  toggleAll,
  selectProjects,
  deselectProjects,
  allSelected,
  isUploading = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Get unique DAW types from detected projects
  const dawTypes = Array.from(
    new Set(
      detectedProjects
        .map((p) => p.dawType)
        .filter((type): type is DAWType => type !== DAWType.UNKNOWN)
    )
  );

  // Get duplicates
  const duplicateProjects = detectedProjects.filter(
    (project) => projectSettings[project.folderPath]?.isDuplicate
  );
  const duplicatePaths = duplicateProjects.map((p) => p.folderPath);

  // Get non-duplicates
  const nonDuplicateProjects = detectedProjects.filter(
    (project) => !projectSettings[project.folderPath]?.isDuplicate
  );
  const nonDuplicatePaths = nonDuplicateProjects.map((p) => p.folderPath);

  // Calculate size thresholds (large = >100MB, small = <10MB)
  const LARGE_THRESHOLD = 100 * 1024 * 1024; // 100MB
  const SMALL_THRESHOLD = 10 * 1024 * 1024; // 10MB

  const largeProjects = detectedProjects.filter(
    (project) => project.totalSize > LARGE_THRESHOLD
  );
  const largePaths = largeProjects.map((p) => p.folderPath);

  const smallProjects = detectedProjects.filter(
    (project) => project.totalSize < SMALL_THRESHOLD
  );
  const smallPaths = smallProjects.map((p) => p.folderPath);

  const handleSelectDuplicates = () => {
    selectProjects(duplicatePaths);
    handleClose();
  };

  const handleDeselectDuplicates = () => {
    deselectProjects(duplicatePaths);
    handleClose();
  };

  const handleSelectNonDuplicates = () => {
    selectProjects(nonDuplicatePaths);
    handleClose();
  };

  const handleDeselectNonDuplicates = () => {
    deselectProjects(nonDuplicatePaths);
    handleClose();
  };

  const handleSelectLarge = () => {
    selectProjects(largePaths);
    handleClose();
  };

  const handleDeselectLarge = () => {
    deselectProjects(largePaths);
    handleClose();
  };

  const handleSelectSmall = () => {
    selectProjects(smallPaths);
    handleClose();
  };

  const handleDeselectSmall = () => {
    deselectProjects(smallPaths);
    handleClose();
  };

  const handleSelectByDAW = (dawType: DAWType) => {
    const projectsOfType = detectedProjects.filter(
      (p) => p.dawType === dawType
    );
    const paths = projectsOfType.map((p) => p.folderPath);
    selectProjects(paths);
    handleClose();
  };

  const handleDeselectByDAW = (dawType: DAWType) => {
    const projectsOfType = detectedProjects.filter(
      (p) => p.dawType === dawType
    );
    const paths = projectsOfType.map((p) => p.folderPath);
    deselectProjects(paths);
    handleClose();
  };

  const getDAWDisplayName = (dawType: DAWType): string => {
    const names: Record<DAWType, string> = {
      [DAWType.ABLETON]: 'Ableton Live',
      [DAWType.LOGIC]: 'Logic Pro',
      [DAWType.PRO_TOOLS]: 'Pro Tools',
      [DAWType.CUBASE]: 'Cubase',
      [DAWType.FL_STUDIO]: 'FL Studio',
      [DAWType.REAPER]: 'Reaper',
      [DAWType.STEMS]: 'Stems',
      [DAWType.UNKNOWN]: 'Unknown',
    };
    return names[dawType] || dawType;
  };

  return (
    <>
      <ButtonGroup
        variant="outlined"
        size="small"
        sx={{
          height: '30.75px',
          '& .MuiButton-root': {
            height: '30.75px',
          },
        }}
      >
        <Button
          onClick={toggleAll}
          disabled={isUploading}
          startIcon={allSelected ? <CheckCircleIcon /> : <UncheckedIcon />}
          sx={{
            height: '30.75px',
            '& .MuiButton-startIcon': {
              marginRight: '8px',
            },
          }}
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </Button>
        <Button
          onClick={handleClick}
          disabled={isUploading}
          endIcon={<ExpandMoreIcon />}
          sx={{
            minWidth: 0,
            px: 1,
            height: '30.75px',
            '& .MuiButton-endIcon': {
              marginLeft: '4px',
            },
          }}
        >
          Filters
        </Button>
      </ButtonGroup>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            borderRadius: 2,
            boxShadow: (theme) => theme.palette.studio.shadows.menu,
          },
        }}
      >
        {/* PRIMARY: Select Non-Duplicates - Most Prominent */}
        <MenuItem
          onClick={handleSelectNonDuplicates}
          sx={{
            py: 1.5,
            px: 2,
            borderLeft: '3px solid',
            borderColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon>
            <CheckCircleIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                <strong>Select Non-Duplicates</strong>
                <Chip
                  label={nonDuplicateProjects.length}
                  size="small"
                  color="primary"
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                  }}
                />
              </Box>
            }
            secondary="Select all unique projects"
            secondaryTypographyProps={{
              sx: { color: 'text.secondary', fontSize: '0.75rem', mt: 0.5 },
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={handleDeselectNonDuplicates}
          sx={{
            px: 2,
            pl: 6,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemText
            primary="Deselect Non-Duplicates"
            secondary={`${nonDuplicateProjects.length} projects`}
            secondaryTypographyProps={{
              sx: { fontSize: '0.75rem' },
            }}
          />
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {/* Duplicates Section */}
        <MenuItem
          onClick={handleSelectDuplicates}
          sx={{
            px: 2,
            '&:hover': {
              backgroundColor: 'studio.status.behind.bg',
            },
          }}
        >
          <ListItemIcon>
            <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                Select Duplicates
                <Chip
                  label={duplicateProjects.length}
                  size="small"
                  color="warning"
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                  }}
                />
              </Box>
            }
            secondary="Projects with existing names"
            secondaryTypographyProps={{
              sx: { fontSize: '0.75rem', mt: 0.5 },
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={handleDeselectDuplicates}
          sx={{
            px: 2,
            pl: 6,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemText
            primary="Deselect Duplicates"
            secondary={`${duplicateProjects.length} projects`}
            secondaryTypographyProps={{
              sx: { fontSize: '0.75rem' },
            }}
          />
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {/* Size-based Selection */}
        <Box sx={{ px: 2, py: 0.5 }}>
          <ListItemText
            primary={
              <Box
                component="span"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: 'text.secondary',
                }}
              >
                By Size
              </Box>
            }
          />
        </Box>

        <MenuItem
          onClick={handleSelectLarge}
          sx={{
            px: 2,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon>
            <StorageIcon fontSize="small" sx={{ color: 'info.main' }} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                Select Large Projects
                {largeProjects.length > 0 && (
                  <Chip
                    label={largeProjects.length}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
              </Box>
            }
            secondary=">100MB"
            secondaryTypographyProps={{
              sx: { fontSize: '0.75rem', mt: 0.5 },
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={handleDeselectLarge}
          sx={{
            px: 2,
            pl: 6,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemText
            primary="Deselect Large Projects"
            secondary={`${largeProjects.length} projects`}
            secondaryTypographyProps={{
              sx: { fontSize: '0.75rem' },
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={handleSelectSmall}
          sx={{
            px: 2,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon>
            <StorageIcon fontSize="small" sx={{ color: 'info.main' }} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" gap={1}>
                Select Small Projects
                {smallProjects.length > 0 && (
                  <Chip
                    label={smallProjects.length}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
              </Box>
            }
            secondary="<10MB"
            secondaryTypographyProps={{
              sx: { fontSize: '0.75rem', mt: 0.5 },
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={handleDeselectSmall}
          sx={{
            px: 2,
            pl: 6,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemText
            primary="Deselect Small Projects"
            secondary={`${smallProjects.length} projects`}
            secondaryTypographyProps={{
              sx: { fontSize: '0.75rem' },
            }}
          />
        </MenuItem>

        {dawTypes.length > 0 && <Divider sx={{ my: 1 }} />}

        {dawTypes.length > 0 && (
          <Box sx={{ px: 2, py: 0.5 }}>
            <ListItemText
              primary={
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                  }}
                >
                  By DAW Type
                </Box>
              }
            />
          </Box>
        )}

        {dawTypes.map((dawType) => {
          const count = detectedProjects.filter(
            (p) => p.dawType === dawType
          ).length;
          return (
            <MenuItem
              key={dawType}
              onClick={() => handleSelectByDAW(dawType)}
              sx={{
                px: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon>
                <MusicNoteIcon
                  fontSize="small"
                  sx={{ color: 'secondary.main' }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    {getDAWDisplayName(dawType)}
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.7rem',
                      }}
                    />
                  </Box>
                }
                secondaryTypographyProps={{
                  sx: { fontSize: '0.75rem' },
                }}
              />
            </MenuItem>
          );
        })}

        {dawTypes.map((dawType) => {
          const count = detectedProjects.filter(
            (p) => p.dawType === dawType
          ).length;
          return (
            <MenuItem
              key={`deselect-${dawType}`}
              onClick={() => handleDeselectByDAW(dawType)}
              sx={{
                px: 2,
                pl: 6,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemText
                primary={`Deselect ${getDAWDisplayName(dawType)}`}
                secondary={`${count} projects`}
                secondaryTypographyProps={{
                  sx: { fontSize: '0.75rem' },
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default ProjectSelectionMenu;
